import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Incident } from '@/types/incident';

interface IncidentState {
  activeIncident: Incident | null;
  activeIncidentId: string | null;
  isDailyOps: boolean;
  _hasHydrated: boolean;

  setActiveIncident: (incident: Incident | null) => void;
  setDailyOps: () => void;
  clearIncidentContext: () => void;
  adoptServerContext: (incidentId: string | null) => void;
}

export const useIncidentStore = create<IncidentState>()(
  persist(
    (set) => ({
      activeIncident: null,
      activeIncidentId: null,
      isDailyOps: true,
      _hasHydrated: false,

      setActiveIncident: (incident) =>
        set({
          activeIncident: incident,
          activeIncidentId: incident?.id ?? null,
          isDailyOps: incident === null,
        }),

      setDailyOps: () =>
        set({
          activeIncident: null,
          activeIncidentId: null,
          isDailyOps: true,
        }),

      clearIncidentContext: () =>
        set({
          activeIncident: null,
          activeIncidentId: null,
          isDailyOps: true,
        }),

      // Reconcile this browser with the server's `user_contexts` row on boot.
      // Engaging an incident PUTs /user/context, but nothing ever read it back, so
      // localStorage was the only source of truth: engaging on one device left every
      // other device pinned to the old incident indefinitely, and a context cleared
      // server-side never reached the client — it kept rendering incident-scoped pages
      // (and prefilling new records) against an incident the server no longer considered
      // engaged. Only called with a successfully fetched server value, so an offline
      // engage is never clobbered by a failed request.
      adoptServerContext: (incidentId) =>
        set((state) => {
          if (incidentId === state.activeIncidentId) return state;
          return incidentId
            ? { activeIncidentId: incidentId, activeIncident: null, isDailyOps: false }
            : { activeIncidentId: null, activeIncident: null, isDailyOps: true };
        }),
    }),
    {
      name: 'incident-storage',
      version: 1,
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          // Migrate from persisting full incident object to ID only
          return {
            activeIncidentId: persistedState?.activeIncident?.id ?? null,
            isDailyOps: persistedState?.isDailyOps ?? true,
          };
        }
        return persistedState as Partial<IncidentState>;
      },
      partialize: (state) => ({
        activeIncidentId: state.activeIncidentId,
        isDailyOps: state.isDailyOps,
      }),
      onRehydrateStorage: () => () => {
        // Deferred via setTimeout to guarantee the store variable is assigned
        // (Zustand persist may fire this synchronously during store creation)
        setTimeout(() => {
          useIncidentStore.setState({ _hasHydrated: true });
        }, 0);
      },
    }
  )
);
